// 일정 상세 상단부 — 히어로(상태 칩 + 제목 + 날짜/장소 + 일일 호스트)와
// 액션 바(공유 / 참가자 / 시작 / 종료 등). 액션은 운영진 권한과 status에 따라
// 분기되며, 비운영진+완료 일정에는 "결과 공유"만 노출.
//
// 시작/종료/되돌리기 같은 비동기 액션은 버튼 ref를 받아 자체적으로 busy
// 상태를 걸기 때문에 콜백이 (btn) => void 시그니처로 들어온다.

import { Fragment } from "preact";
import { matchPlayerName } from "../matches";
import { openResultShareSheet } from "../share";
import { Icon } from "../ui/components/Icon";
import { mountPreact } from "../ui/preact-mount";
import { eventKindLabel, formatDateKo, statusLabel } from "../ui/format";
import { toast } from "../ui/sheet";

interface ParticipantLike {
  event_participant_id: string;
  member_id?: string | null;
  display_name?: string | null;
}

interface MatchLike {
  result_status?: string;
}

export interface EventHeaderParams {
  ev: any;
  detail: any;
  matches: MatchLike[];
  participants: ParticipantLike[];
  canManage: boolean;
  onEdit: () => void;
  onAddParticipant: () => void;
  onGuestLink: () => void;
  onStart: (btn: HTMLButtonElement) => void;
  onRevert: (btn: HTMLButtonElement) => void;
  onComplete: () => void;
}

function Hero({ ev, participants, canManage, onEdit }: EventHeaderParams) {
  const chipCls = ev.status === "in_progress"
    ? "detail-hero__chip detail-hero__chip--live"
    : "detail-hero__chip";
  const canEdit = canManage && ev.status !== "completed" && ev.status !== "archived";
  // 정기 모임 호스트 = creator. 참가자 명단에서 이름을 찾을 수 있을 때만 표시
  // (현장에 호스트가 있다는 신호) — 운영진이 만들었지만 참석 안 하는 경우엔
  // 표시 안 함 (오히려 오해 소지). 특별전은 운영진=호스트 자명해서 생략.
  const hostName = (
    ev.event_type === "regular" && ev.created_by
      ? participants.find((p) => p.member_id === ev.created_by)?.display_name
      : null
  );
  return (
    <div class="detail-hero">
      <span class={chipCls}>{statusLabel(ev.status)}</span>
      {canEdit && (
        <button
          type="button"
          class="detail-hero__edit"
          aria-label="수정"
          title="수정"
          onClick={onEdit}
        >
          <Icon name="edit" size={16} />
        </button>
      )}
      <h1 class="detail-hero__title">{ev.title}</h1>
      <p class="detail-hero__meta">
        <span>{formatDateKo(ev.event_date)}</span>
        {ev.location && <span>· {ev.location}</span>}
        <span>· {eventKindLabel(ev)}</span>
        {hostName && <span>· 호스트 {hostName}</span>}
      </p>
    </div>
  );
}

function Actions(params: EventHeaderParams): preact.JSX.Element | null {
  const {
    ev, detail, matches, canManage,
    onAddParticipant, onGuestLink, onStart, onRevert, onComplete,
  } = params;

  if (!canManage) {
    if (ev.status === "completed") {
      return (
        <div class="btn-row">
          <button
            class="btn btn--primary"
            type="button"
            onClick={() => openResultShareSheet(detail)}
          >
            <Icon name="share" size={16} />
            결과 공유
          </button>
        </div>
      );
    }
    // 정기모임은 진행 단계에서 비호스트 회원도 게스트 이름 수정을 위해
    // 참가자 시트 진입 가능 (자유도 정책). 시트 내부에서 슬림 모드로
    // (게스트 리스트 + ✏️ 만) 자동 렌더됨.
    if (
      ev.event_type === "regular" &&
      (ev.status === "draft" || ev.status === "ready" || ev.status === "in_progress")
    ) {
      return (
        <div class="btn-row">
          <button
            class="btn btn--ghost"
            type="button"
            onClick={onAddParticipant}
          >
            <Icon name="users" size={16} />
            참가자
          </button>
        </div>
      );
    }
    return null;
  }

  const buttons: preact.JSX.Element[] = [];

  if (ev.status === "completed") {
    buttons.push(
      <button
        key="share"
        class="btn btn--primary"
        type="button"
        onClick={() => openResultShareSheet(detail)}
      >
        <Icon name="share" size={16} />
        결과 공유
      </button>,
    );
  }
  if (ev.status === "draft" || ev.status === "ready" || ev.status === "in_progress") {
    buttons.push(
      <button
        key="participants"
        class="btn btn--ghost"
        type="button"
        onClick={onAddParticipant}
      >
        <Icon name="users" size={16} />
        참가자
      </button>,
    );
  }
  if (ev.status === "ready" || ev.status === "in_progress") {
    buttons.push(
      <button
        key="guestlink"
        class="btn btn--ghost"
        type="button"
        onClick={onGuestLink}
      >
        <Icon name="link" size={15} />
        공유
      </button>,
    );
  }
  if (ev.status === "draft" || ev.status === "ready") {
    const noSchedule = matches.length === 0;
    const needsRegen = ev.schedule_stale && matches.length > 0;
    if (noSchedule || needsRegen) {
      const msg = noSchedule
        ? "먼저 스케줄을 생성해주세요."
        : "참가자가 변경되었어요. 스케줄을 재생성해주세요.";
      buttons.push(
        <button
          key="start-disabled"
          class="btn btn--primary"
          type="button"
          disabled
          title={noSchedule ? "스케줄 생성이 필요합니다" : "스케줄 재생성이 필요합니다"}
          style="opacity:0.5;cursor:not-allowed"
          onClick={() => toast(msg)}
        >
          <Icon name="play" size={14} />
          시작
        </button>,
      );
    } else {
      buttons.push(
        <button
          key="start"
          class="btn btn--primary"
          type="button"
          onClick={(e: Event) => onStart(e.currentTarget as HTMLButtonElement)}
        >
          <Icon name="play" size={14} />
          시작
        </button>,
      );
    }
  } else if (ev.status === "in_progress") {
    const hasAnyResult = matches.some((m) =>
      m.result_status && m.result_status !== "empty" && m.result_status !== "skipped",
    );
    if (!hasAnyResult) {
      buttons.push(
        <button
          key="revert"
          class="btn btn--ghost"
          type="button"
          onClick={(e: Event) => onRevert(e.currentTarget as HTMLButtonElement)}
        >
          <Icon name="rotate-ccw" size={15} />
          시작 취소
        </button>,
      );
    }
    buttons.push(
      <button
        key="complete"
        class="btn btn--ghost"
        type="button"
        onClick={onComplete}
      >
        <Icon name="check" size={16} />
        종료하기
      </button>,
    );
  }

  if (buttons.length === 0) return null;
  return <div class="btn-row">{buttons}</div>;
}

function EventDetailHeader(params: EventHeaderParams) {
  return (
    <Fragment>
      <Hero {...params} />
      <Actions {...params} />
    </Fragment>
  );
}

export function buildEventDetailHeader(params: EventHeaderParams): HTMLElement[] {
  return [mountPreact(<EventDetailHeader {...params} />)];
}
